/**
 * PATH: apps/quote/src/pages/builder/BuilderLanding.tsx
 * PURPOSE: Builder landing page with "Continue Last Quote" and recent quotes
 *
 * WHY:
 * - Users should have a fast way to continue their last work
 * - Shows recent quotes for quick access
 * - Provides actions: New Quote, Import Excel, Start from Template
 *
 * FLOW:
 *   Load landing → Show Continue card (if last quote exists) → Show recent quotes → Actions
 */

import { useState, useEffect, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  FileSpreadsheet,
  Plus,
  Upload,
  Clock,
  ArrowRight,
  FileText,
  Search,
  Filter,
  Layers,
  Calendar,
  DollarSign,
  User,
  Building2,
  AlertCircle,
  RefreshCw,
  Sparkles,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { quoteApi, type QuoteListItem } from '@/lib/quoteApi'
import { useAuth } from '@quotech/auth'

// Storage key for last opened quote
const LAST_OPENED_KEY = 'quotech:builder:lastOpened'

interface LastOpenedQuote {
  quoteId: string
  quoteNumber: string
  projectName: string | null
  clientName: string | null
  status: string
  openedAt: string
  organisationId: string
}

/**
 * Load last opened quote from localStorage
 */
function loadLastOpenedQuote(organisationId: string | undefined): LastOpenedQuote | null {
  if (!organisationId) return null
  try {
    const stored = localStorage.getItem(LAST_OPENED_KEY)
    if (!stored) return null
    const data = JSON.parse(stored) as LastOpenedQuote
    // Only return if same org
    if (data.organisationId !== organisationId) return null
    return data
  } catch {
    return null
  }
}

/**
 * Save last opened quote to localStorage
 */
export function saveLastOpenedQuote(quote: LastOpenedQuote): void {
  try {
    localStorage.setItem(LAST_OPENED_KEY, JSON.stringify(quote))
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear last opened quote from localStorage
 */
function clearLastOpenedQuote(): void {
  try {
    localStorage.removeItem(LAST_OPENED_KEY)
  } catch {
    // Ignore
  }
}

function StatusBadge({ status }: { status: string }) {
  const classes: Record<string, string> = {
    draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    pending_approval: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    approved: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    sent: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
    accepted: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    expired: 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
  }
  
  const labels: Record<string, string> = {
    draft: 'Draft',
    pending_approval: 'Pending Approval',
    approved: 'Approved',
    sent: 'Sent',
    accepted: 'Accepted',
    rejected: 'Rejected',
    expired: 'Expired',
  }

  return (
    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', classes[status] || classes.draft)}>
      {labels[status] || status}
    </span>
  )
}

function ContinueCard({ lastQuote, onClear }: { lastQuote: LastOpenedQuote; onClear: () => void }) {
  const navigate = useNavigate()
  const timeSince = useMemo(() => {
    const opened = new Date(lastQuote.openedAt)
    const now = new Date()
    const diffMs = now.getTime() - opened.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)
    
    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
  }, [lastQuote.openedAt])

  return (
    <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white shadow-lg shadow-blue-500/20">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white/10 rounded-xl">
            <FileSpreadsheet className="w-8 h-8" />
          </div>
          <div>
            <p className="text-blue-100 text-sm mb-1">Continue where you left off</p>
            <h3 className="text-xl font-bold mb-1">{lastQuote.quoteNumber}</h3>
            <p className="text-blue-200 text-sm">
              {lastQuote.clientName || 'No client'} • {lastQuote.projectName || 'Untitled project'}
            </p>
            <div className="flex items-center gap-3 mt-3">
              <StatusBadge status={lastQuote.status} />
              <span className="text-blue-200 text-xs flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {timeSince}
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            onClick={() => navigate(`/builder/${lastQuote.quoteId}`)}
            className="flex items-center gap-2 px-6 py-3 bg-white text-blue-700 font-semibold rounded-xl hover:bg-blue-50 transition-colors"
          >
            Continue
            <ArrowRight className="w-5 h-5" />
          </button>
          <button
            onClick={onClear}
            className="text-xs text-blue-200 hover:text-white transition-colors"
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  )
}

function QuoteCard({ quote }: { quote: QuoteListItem }) {
  const navigate = useNavigate()
  
  return (
    <div
      onClick={() => navigate(`/builder/${quote.id}`)}
      className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-lg transition-all cursor-pointer group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 transition-colors">
            <FileText className="w-5 h-5 text-slate-500 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
          </div>
          <div>
            <h4 className="font-semibold text-slate-900 dark:text-white">{quote.quote_number}</h4>
            <p className="text-xs text-slate-500 dark:text-slate-400">{quote.quote_type_display || 'Quote'}</p>
          </div>
        </div>
        <StatusBadge status={quote.status} />
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
          <User className="w-4 h-4 text-slate-400" />
          <span className="truncate">{quote.client_name || 'No client'}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
          <Building2 className="w-4 h-4 text-slate-400" />
          <span className="truncate">{quote.project_name || 'Untitled project'}</span>
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
            <Calendar className="w-4 h-4" />
            <span className="text-xs">{quote.created_at ? new Date(quote.created_at).toLocaleDateString() : '-'}</span>
          </div>
          <div className="flex items-center gap-1 font-semibold text-slate-900 dark:text-white">
            <DollarSign className="w-4 h-4 text-emerald-500" />
            <span>{quote.total_excl_gst ? formatCurrency(Number(quote.total_excl_gst)) : '-'}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function ActionCard({
  icon: Icon,
  title,
  description,
  onClick,
  primary = false,
}: {
  icon: React.ElementType
  title: string
  description: string
  onClick: () => void
  primary?: boolean
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-start gap-4 p-5 rounded-xl border text-left transition-all',
        primary
          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30'
          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md'
      )}
    >
      <div className={cn(
        'p-3 rounded-xl',
        primary ? 'bg-blue-100 dark:bg-blue-900/50' : 'bg-slate-100 dark:bg-slate-700'
      )}>
        <Icon className={cn(
          'w-6 h-6',
          primary ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'
        )} />
      </div>
      <div>
        <h4 className={cn(
          'font-semibold mb-1',
          primary ? 'text-blue-900 dark:text-blue-200' : 'text-slate-900 dark:text-white'
        )}>
          {title}
        </h4>
        <p className="text-sm text-slate-500 dark:text-slate-400">{description}</p>
      </div>
    </button>
  )
}

export default function BuilderLanding() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const organisationId = user?.organisation_id
  
  const [searchQuery, setSearchQuery] = useState('')
  const [lastQuote, setLastQuote] = useState<LastOpenedQuote | null>(null)
  
  // Load last opened quote on mount
  useEffect(() => {
    setLastQuote(loadLastOpenedQuote(organisationId))
  }, [organisationId])
  
  // Fetch recent quotes
  const quotesQuery = useQuery({
    queryKey: ['quotes', 'recent', { limit: 12 }],
    queryFn: () => quoteApi.listQuotes({ page: 1, page_size: 12, ordering: '-updated_at' }),
  })
  
  // Validate last quote still exists
  useQuery({
    queryKey: ['quote', 'validate', lastQuote?.quoteId],
    queryFn: async () => {
      if (!lastQuote) return null
      try {
        await quoteApi.getQuote(lastQuote.quoteId)
        return true
      } catch {
        clearLastOpenedQuote()
        setLastQuote(null)
        return false
      }
    },
    enabled: !!lastQuote,
    retry: false,
  })
  
  // Filter quotes by search
  const filteredQuotes = useMemo(() => {
    if (!quotesQuery.data?.results) return []
    if (!searchQuery.trim()) return quotesQuery.data.results
    
    const query = searchQuery.toLowerCase()
    return quotesQuery.data.results.filter(q =>
      q.quote_number?.toLowerCase().includes(query) ||
      q.client_name?.toLowerCase().includes(query) ||
      q.project_name?.toLowerCase().includes(query)
    )
  }, [quotesQuery.data?.results, searchQuery])
  
  const handleClearLastQuote = () => {
    clearLastOpenedQuote()
    setLastQuote(null)
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Quote Builder</h1>
          <p className="text-slate-500 dark:text-slate-400">
            Create and manage professional quotes with Excel-like precision
          </p>
        </div>
        <button
          onClick={() => navigate('/builder/new')}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Quote
        </button>
      </div>

      {/* Continue Card */}
      {lastQuote && (
        <ContinueCard lastQuote={lastQuote} onClear={handleClearLastQuote} />
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ActionCard
            icon={Plus}
            title="New Quote"
            description="Start a blank quote from scratch"
            onClick={() => navigate('/builder/new')}
            primary
          />
          <ActionCard
            icon={Upload}
            title="Import Excel"
            description="Import an existing Excel quote sheet"
            onClick={() => navigate('/quotes?action=import')}
          />
          <ActionCard
            icon={Sparkles}
            title="Start from Template"
            description="Use a pre-configured quote template"
            onClick={() => navigate('/templates')}
          />
        </div>
      </div>

      {/* Recent Quotes */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Recent Quotes</h2>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search quotes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 w-64 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 outline-none focus:border-blue-400 dark:focus:border-blue-600"
              />
            </div>
            <Link
              to="/quotes"
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              View all
            </Link>
          </div>
        </div>

        {quotesQuery.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
          </div>
        ) : quotesQuery.isError ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="w-10 h-10 text-red-500 mb-3" />
            <p className="text-slate-600 dark:text-slate-400 mb-2">Failed to load quotes</p>
            <button
              onClick={() => quotesQuery.refetch()}
              className="text-sm text-blue-600 hover:underline"
            >
              Try again
            </button>
          </div>
        ) : filteredQuotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Layers className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-3" />
            <p className="text-slate-600 dark:text-slate-400 mb-1">
              {searchQuery ? 'No quotes match your search' : 'No quotes yet'}
            </p>
            <p className="text-sm text-slate-400 dark:text-slate-500">
              {searchQuery ? 'Try a different search term' : 'Create your first quote to get started'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredQuotes.map((quote) => (
              <QuoteCard key={quote.id} quote={quote} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

